import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Loader2, Star, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  shipped: 'bg-purple-100 text-purple-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const statusLabels = {
  pending: 'Pendente',
  confirmed: 'Confirmado',
  shipped: 'Enviado',
  delivered: 'Entregue',
  cancelled: 'Cancelado',
};

export const Orders = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewModal, setReviewModal] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);

  const handleCancelOrder = async (orderId) => {
    const ok = window.confirm('Tem certeza que deseja cancelar este pedido?');
    if (!ok) return;
    try {
      await axios.delete(`${API}/orders/${orderId}`, { withCredentials: true });
      toast.success('Pedido cancelado');
      setOrders((prev) => prev.filter((o) => o.id !== orderId));
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Não foi possível cancelar o pedido';
      toast.error(msg);
    }
  };

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    fetchOrders();
  }, [user]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, { withCredentials: true });
      const list = Array.isArray(response.data) ? response.data : [];
      const visible = list.filter((o) => o && o.status !== 'cancelled');
      setOrders(visible);

      const pending = visible.filter((o) => o && o.payment_status === 'pending' && o.status === 'pending');
      if (pending.length > 0) {
        try {
          const syncResp = await axios.post(
            `${API}/mercadopago/sync-orders`,
            { order_ids: pending.map((o) => o.id) },
            { withCredentials: true, timeout: 20000 }
          );
          const results = syncResp?.data?.results || [];
          if (Array.isArray(results) && results.length > 0) {
            setOrders((prev) => prev.map((o) => {
              const r = results.find((x) => x.order_id === o.id);
              if (!r || !r.synced) return o;
              if (r.payment_status && r.order_status) {
                return { ...o, payment_status: r.payment_status, status: r.order_status };
              }
              return o;
            }));
          }
        } catch (e) {
          // não bloqueia a tela se o sync falhar
        }
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!reviewModal) return;
    setSubmittingReview(true);
    try {
      await axios.post(`${API}/reviews/${reviewModal.product_id}`, {
        rating: reviewRating,
        comment: reviewComment
      }, { withCredentials: true });
      toast.success('Avaliação enviada com sucesso!');
      setReviewModal(null);
      setReviewRating(5);
      setReviewComment('');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao enviar avaliação';
      toast.error(msg);
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0f172a] flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0f172a] pt-24 pb-12">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-8">Meus Pedidos</h1>

        {orders.length > 0 ? (
          <div className="space-y-6" data-testid="orders-list">
            {orders.map(order => (
              <Card
                key={order.id}
                data-testid={`order-${order.id}`}
                className="cursor-pointer"
                onClick={() => navigate(`/orders/${order.id}`)}
              >
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle>Pedido #{order.id.slice(0, 8)}</CardTitle>
                      {order.order_code && (
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Código: {order.order_code}</p>
                      )}
                      <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                        {new Date(order.created_at).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                    <Badge className={statusColors[order.status]} data-testid={`order-status-${order.id}`}>
                      {statusLabels[order.status]}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center">
                        <div>
                          <p className="font-semibold text-slate-900 dark:text-white">{item.product_name}</p>
                          <p className="text-sm text-slate-600 dark:text-slate-400">Quantidade: {item.quantity}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <p className="font-semibold text-blue-600 dark:text-blue-400">
                            R$ {(item.price * item.quantity).toFixed(2)}
                          </p>
                          {order.status === 'delivered' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                setReviewModal({ product_id: item.product_id, product_name: item.product_name });
                              }}
                              className="text-xs"
                            >
                              <Star className="h-3 w-3 mr-1" />
                              Avaliar
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                    <div className="border-t border-slate-200 dark:border-slate-700 pt-3 mt-3">
                      <div className="flex justify-between items-center text-lg font-bold">
                        <span>Total</span>
                        <span className="text-blue-600 dark:text-blue-400">R$ {order.total.toFixed(2)}</span>
                      </div>
                      <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">
                        Recebimento: <span className="font-semibold">{order.address?.delivery_method === 'pickup' ? 'Retirada' : 'Entrega'}</span>
                      </p>
                      <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">
                        Status do Pagamento: <span className="font-semibold">{order.payment_status === 'paid' ? 'Pago' : 'Pendente'}</span>
                      </p>

                      {order.status === 'pending' && order.payment_status === 'pending' && (
                        <div className="mt-4 flex justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancelOrder(order.id);
                            }}
                          >
                            Cancelar pedido
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="text-center py-12">
              <Package className="h-16 w-16 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-600 dark:text-slate-400 mb-4">Você ainda não tem pedidos</p>
            </CardContent>
          </Card>
        )}

        {/* Modal de Avaliação */}
        {reviewModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 max-w-md w-full shadow-2xl">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white">Avaliar Produto</h3>
                <button onClick={() => setReviewModal(null)} className="text-slate-400 hover:text-slate-600">
                  <X className="h-6 w-6" />
                </button>
              </div>
              <p className="text-slate-600 dark:text-slate-400 mb-4">{reviewModal.product_name}</p>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Nota</label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setReviewRating(star)}
                      className="focus:outline-none"
                    >
                      <Star
                        className={`h-8 w-8 transition-colors ${
                          star <= reviewRating ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Comentário</label>
                <textarea
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  placeholder="Conte sua experiência com o produto..."
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white resize-none"
                  rows={4}
                />
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setReviewModal(null)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleSubmitReview}
                  disabled={submittingReview}
                  className="flex-1"
                >
                  {submittingReview ? 'Enviando...' : 'Enviar Avaliação'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};